'use client'

import { useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ProductForm, type ProductFormRef } from '@/components/partials/products'
import { ContentError, FormSkeleton } from '@/components/partials'
import { useProduct, useUpdateProduct } from '@/hooks'
import { type CreateProductParams } from '@/types/product'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const productFormRef = useRef<ProductFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: product, isLoading, error } = useProduct(id)
  const { mutate: updateProduct, isPending } = useUpdateProduct()

  const handleSubmit = (data: CreateProductParams) => {
    updateProduct(
      { id, params: data },
      {
        onSuccess: async updatedProduct => {
          setIsRedirecting(true)
          await queryClient.invalidateQueries({ queryKey: ['product', id] })
          await queryClient.invalidateQueries({ queryKey: ['products'] })
          toast.success(t('updated_successfully'))
          router.push(`/products/${updatedProduct.id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (productFormRef.current) {
        productFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/products/${id}`)
  }

  if (isLoading || !product) {
    return <FormSkeleton fieldRows={5} showPictures />
  }

  if (error || !product) {
    return <ContentError error={error} listUrl="/products" />
  }

  return (
    <ProductForm
      ref={productFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={product}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
